import ContentCopyOutlined from "@mui/icons-material/ContentCopyOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import { Alert, Box, Chip, IconButton, Paper, Tooltip, Typography } from "@mui/material";
import { DateTime } from "luxon";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { SystemFlatSwitch } from "../../../components/UIToolkit/Clickable/SystemFlatSwitch";
import { FileDropZone } from "../../../components/UIToolkit/Inputs/FileDropZone";
import { SystemDropdown } from "../../../components/UIToolkit/Inputs/SystemDropDown/SystemDropdown";
import { SystemValidationTextField } from "../../../components/UIToolkit/Inputs/SystemValidationTextField";
import SystemButton from "../../../components/UIToolkit/SystemButton/SystemButton";
import { earliestDateInBlob, SHIFTS_DATE_COLUMN } from "../loadOrder";
import { ACCEPTED_TYPES, DROP_ZONE_HINT } from "../utils";
import { WeekParityConfirmDialog } from "../WeekParityConfirmDialog";
import { LoadOrderCaption } from "./LoadOrderCaption";
import { type LoadRecurringResult, LoadResultDisplay, type LoadShiftsResult } from "./LoadResultDisplay";
import type { LoadNursesResults } from "./NursesSummary";
import { PreviewCard } from "./PreviewCard";
import { RosterLoadResults } from "./RosterLoadResults";
import {
	type AutoPromptFileKind,
	EFFORT_LABEL_KEY,
	EFFORT_ORDER,
	formatFileSize,
	KIND_LABEL,
	KIND_ORDER,
	previewFileType,
	SECTION_HEADERS,
	SECTION_ORDER,
	useAutoPromptSection,
} from "./useAutoPromptSection";

export function AutoPromptSection() {
	const {
		phase,
		files,
		orderedFiles,
		loadOrderEntries,
		hasAtLeastOneFile,
		addFiles,
		handleKindChange,
		handleDeleteFile,
		effort,
		setEffort,
		editable,
		setEditable,
		flags,
		isAnalyzing,
		hasRanAnalysis,
		handleAnalyze,
		finalPrompt,
		setFinalPrompt,
		handleGeneratePrompt,
		handleCopyPrompt,
		handleBackToEditing,
		transformed,
		isTransforming,
		handleTransformAll,
		updateOutcome,
		handleCancelTransform,
		isUploadAllBusy,
		allRemainingUploaded,
		failedTransformFiles,
		handleUploadAll,
		chosenDepartment,
		showWeekParityDialog,
		setShowWeekParityDialog,
		handleSwitchAndUploadTemplate,
		handleKeepAsIsAndUploadTemplate,
		isUpdatingFirstSunday,
		departmentId,
		resetWizard,
	} = useAutoPromptSection();

	const { t } = useTranslation();

	const effortOptions = useMemo(
		() =>
			EFFORT_ORDER.map((value) => ({
				label: t(EFFORT_LABEL_KEY[value]),
				value,
				"data-testid": `auto-prompt-effort-${value}`,
				disabled: isAnalyzing,
			})),
		[t, isAnalyzing],
	);

	return (
		<Paper data-testid="auto-prompt-section" sx={{ p: 3, minHeight: "fit-content", overflow: "visible" }}>
			<Typography variant="h5" sx={{ mb: 1 }}>
				Auto Prompt
			</Typography>
			<Typography variant="body2" sx={{ mb: 2, color: "text.secondary" }}>
				Upload your roster, template, and shift files to begin analysis.
			</Typography>

			{/* Phase 1 - Upload */}
			<FileDropZone multiple accept={ACCEPTED_TYPES.join(",")} hint={DROP_ZONE_HINT} onFiles={addFiles} dataTestId="auto-prompt-drop-zone" />

			{files.length > 0 && (
				<Box sx={{ display: "flex", flexDirection: "column", gap: 1, mb: 2 }}>
					{files.map((f) => (
						<Paper key={f.id} variant="outlined" sx={{ p: 1.5, display: "flex", alignItems: "center", gap: 2 }}>
							<Box sx={{ flex: 1, minWidth: 0 }}>
								<Typography variant="body2" sx={{ fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
									{f.file.name}
								</Typography>
								<Typography variant="caption" color="text.secondary">
									{formatFileSize(f.file.size)}
								</Typography>
							</Box>
							<SystemDropdown<AutoPromptFileKind>
								label="Type"
								items={KIND_ORDER.map((kind) => ({ value: kind, label: KIND_LABEL[kind] }))}
								selectedItem={f.kind}
								onChange={(value) => value && handleKindChange(f.id, value)}
								formControlSx={{ minWidth: 140 }}
								data-testid={`auto-prompt-file-kind-${f.id}`}
							/>
							<IconButton aria-label={`Remove ${f.file.name}`} onClick={() => handleDeleteFile(f.id)}>
								<DeleteOutlineOutlinedIcon />
							</IconButton>
						</Paper>
					))}
				</Box>
			)}

			<Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "flex-end", gap: 2, mb: 0.5 }}>
				<Box sx={{ display: "flex", gap: 1 }}>
					<SystemButton
						data-testid="auto-prompt-analyze-button"
						onClick={handleAnalyze}
						disabled={!hasAtLeastOneFile || !departmentId || isAnalyzing}
						loading={isAnalyzing}
					>
						{hasRanAnalysis ? "Re-Analyze Files" : "Analyze Files"}
					</SystemButton>
					{phase !== "upload" && (
						<SystemButton buttonVariant="secondary" onClick={resetWizard} data-testid="auto-prompt-reset-button">
							Reset
						</SystemButton>
					)}
				</Box>
				<Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, ml: { sm: "auto" } }}>
					<Typography variant="overline" sx={{ color: "text.secondary", lineHeight: 1.4 }}>
						{t("onboarding_auto_prompt.effort.label")}
					</Typography>
					{/* Locked mid-run: the request is already in flight, so a switch left free to move would
					    label the returned report with a level that didn't produce it. */}
					<SystemFlatSwitch
						options={effortOptions}
						activeIndexOrValue={effort}
						onSwitch={(option) => setEffort(option.value)}
						delayCallback={0}
						equalWidthOptions
						data-testid="auto-prompt-effort-switch"
					/>
				</Box>
			</Box>

			{/* Only while the choice is still actionable — it's durable noise above the transform results. */}
			{(phase === "upload" || phase === "analysis") && (
				<Typography variant="caption" sx={{ display: "block", mb: 2, color: "text.secondary" }}>
					{t("onboarding_auto_prompt.effort.hint")}
				</Typography>
			)}

			{/* Phase 2 - Analysis (editable sections) */}
			{phase === "analysis" && editable && (
				<Box sx={{ display: "flex", flexDirection: "column", gap: 2 }} data-testid="auto-prompt-analysis">
					{flags.length > 0 && (
						<Alert severity="warning">
							<Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
								Attention Required
							</Typography>
							<Box component="ul" sx={{ pl: 3, m: 0 }}>
								{flags.map((f) => (
									<Typography key={f} component="li" variant="body2">
										{f}
									</Typography>
								))}
							</Box>
						</Alert>
					)}

					{SECTION_ORDER.map((key) => (
						<Paper key={key} variant="outlined" sx={{ p: 2 }}>
							<Typography variant="overline" sx={{ display: "block", mb: 1, color: "text.secondary" }}>
								{SECTION_HEADERS[key]}
							</Typography>
							<SystemValidationTextField
								fullWidth
								value={editable[key]}
								onChange={(e) => setEditable((prev) => (prev ? { ...prev, [key]: e.target.value } : prev))}
								injectedProps={{ multiline: true, minRows: 3 }}
								dataTestId={`auto-prompt-section-${key.replaceAll("_", "-")}`}
							/>
						</Paper>
					))}

					<Box sx={{ display: "flex", justifyContent: "flex-end" }}>
						<SystemButton onClick={handleGeneratePrompt} data-testid="auto-prompt-generate-prompt-button">
							Generate Prompt
						</SystemButton>
					</Box>
				</Box>
			)}

			{/* Phase 3 - Generated Prompt */}
			{phase === "prompt" && (
				<Box sx={{ display: "flex", flexDirection: "column", gap: 2 }} data-testid="auto-prompt-final-prompt">
					<SystemValidationTextField
						label="Final transform prompt"
						value={finalPrompt}
						onChange={(e) => setFinalPrompt(e.target.value)}
						fullWidth
						injectedProps={{ multiline: true, minRows: 12 }}
					/>
					<Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end", alignItems: "center" }}>
						<Tooltip title="Copy prompt">
							<IconButton onClick={handleCopyPrompt} aria-label="Copy prompt" data-testid="auto-prompt-copy-prompt-button">
								<ContentCopyOutlined />
							</IconButton>
						</Tooltip>
						<SystemButton buttonVariant="secondary" onClick={handleBackToEditing} data-testid="auto-prompt-back-to-editing-button">
							Back to Editing
						</SystemButton>
						<SystemButton
							onClick={handleTransformAll}
							disabled={!finalPrompt.trim() || isTransforming}
							loading={isTransforming}
							data-testid="auto-prompt-transform-button"
						>
							Transform
						</SystemButton>
					</Box>
				</Box>
			)}

			{/* Phase 4 - Bulk Transform + Combined Preview */}
			{phase === "transform" && (
				<Box sx={{ display: "flex", flexDirection: "column", gap: 2 }} data-testid="auto-prompt-transform-results">
					{/* Editable prompt that generated the tables below, so a bad prompt can be fixed and re-run in place. */}
					<Paper variant="outlined" sx={{ p: 2 }} data-testid="auto-prompt-retransform">
						<Typography variant="overline" sx={{ display: "block", mb: 1, color: "text.secondary" }}>
							Transform prompt
						</Typography>
						<SystemValidationTextField
							fullWidth
							value={finalPrompt}
							onChange={(e) => setFinalPrompt(e.target.value)}
							injectedProps={{ multiline: true, minRows: 8 }}
							dataTestId="auto-prompt-retransform-prompt"
						/>
						<Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1 }}>
							<SystemButton
								onClick={handleTransformAll}
								disabled={!finalPrompt.trim() || isTransforming}
								loading={isTransforming}
								data-testid="auto-prompt-retransform-button"
							>
								Re-transform
							</SystemButton>
						</Box>
					</Paper>

					{isTransforming && (
						<Box sx={{ display: "flex", gap: 1 }}>
							{files.map((f) => (
								<Chip key={f.id} label={`Transforming ${KIND_LABEL[f.kind]}...`} />
							))}
						</Box>
					)}

					{/* One card per file, rendered in the exact order Upload All will load them. */}
					{orderedFiles.map((file, index) => {
						const outcome = transformed[file.id];
						if (!outcome) return null;
						const previewType = previewFileType(file.kind);
						return (
							<Box key={file.id}>
								<Typography variant="subtitle2" sx={{ mb: 0.5, color: "text.secondary" }}>
									{`${index + 1}. ${KIND_LABEL[file.kind]}`}
									{outcome.uploaded ? " - uploaded" : outcome.skip ? " - skipped" : ""}
								</Typography>
								<PreviewCard
									fileName={file.file.name}
									fileType={previewType}
									transformedData={outcome.csvBuffer}
									dataTestIdPrefix={`auto-prompt-preview-${previewType}-${file.id}`}
									failedTabs={outcome.failedTabs}
									skip={outcome.skip}
									onSkipChange={(skip) => updateOutcome(file.id, { skip })}
									onCancel={() => handleCancelTransform(file.id)}
									onFilteredBlobChange={(blob) => {
										updateOutcome(file.id, { filteredBlob: blob });
										// Follows the filtered payload: dropping a file's earliest rows raises its earliest
										// date, which can move it past another file in the load order.
										if (file.kind === "shifts") {
											void earliestDateInBlob(blob, SHIFTS_DATE_COLUMN).then((date) => updateOutcome(file.id, { earliestDate: date }));
										}
									}}
								/>
								{outcome.uploaded && outcome.loadResult && (
									<Box sx={{ mt: 2 }}>
										{file.kind === "roster" ? (
											<RosterLoadResults
												results={outcome.loadResult as LoadNursesResults}
												timezone={chosenDepartment?.timezone}
												dataTestId={`auto-prompt-result-${file.id}`}
											/>
										) : (
											<LoadResultDisplay
												result={outcome.loadResult as LoadShiftsResult | LoadRecurringResult}
												departmentId={departmentId}
												timezone={chosenDepartment?.timezone}
											/>
										)}
									</Box>
								)}
							</Box>
						);
					})}

					{failedTransformFiles.length > 0 && (
						<Alert severity="error" data-testid="auto-prompt-transform-failures">
							{`${failedTransformFiles.length} file(s) produced no CSV: ${failedTransformFiles.map((f) => f.file.name).join(", ")}. Re-transform or remove them — Upload All skips them.`}
						</Alert>
					)}

					<LoadOrderCaption entries={loadOrderEntries} dataTestId="auto-prompt-load-order" />

					<Box sx={{ display: "flex", justifyContent: "flex-end" }}>
						<SystemButton
							data-testid="auto-prompt-upload-all-button"
							onClick={handleUploadAll}
							disabled={isUploadAllBusy || allRemainingUploaded}
							loading={isUploadAllBusy}
						>
							{allRemainingUploaded ? "All Uploaded" : "Upload All Files"}
						</SystemButton>
					</Box>
				</Box>
			)}

			{chosenDepartment?.first_sunday && (
				<WeekParityConfirmDialog
					open={showWeekParityDialog}
					department={chosenDepartment}
					today={DateTime.now()}
					isSwitching={isUpdatingFirstSunday}
					onSwitch={handleSwitchAndUploadTemplate}
					onKeepAsIs={handleKeepAsIsAndUploadTemplate}
					onCancel={() => setShowWeekParityDialog(false)}
				/>
			)}
		</Paper>
	);
}
